import { BreadcrumbNavigationInteraction } from "@components/BreadcrumbNavigationInteraction/BreadcrumbNavigationInteraction";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Breadcrumb } from "@jmc/solid-design-system/src/components/atoms/Breadcrumb/Breadcrumb";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { default as path } from "@jmc/utils/utils/path";
import { mdiHome } from "@mdi/js";
import { CMSFootnotes } from "@types";
import { default as navigate } from "@utils/navigate";
import { getLocalisedUrlFunc } from "@utils/navigate";
import classNames from "classnames";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import sanitizeHtml from "sanitize-html";

import style from "./style.module.scss";

interface Props {
    paths?: { url: string; title: string }[];
    footnotes?: CMSFootnotes[];
    homePath?: string;
}

export const JMCBreadcrumb: React.FunctionComponent<Props> = ({ paths, footnotes, homePath }: Props) => {
    const locale = useLocale();
    const getLocalisedUrl = getLocalisedUrlFunc(locale);
    const navigationName = "breadcrumb_navigation";
    const isMobile = useMediaQuery(BreakPoint.md);
    const { t } = useTranslation();
    const homeLink = "Home";
    const { jnjFullBranded } = useJnjBranding();

    const generateJsonLd = (
        paths: { url: string; title: string }[] = null,
        locale: string,
    ): Record<string, unknown> => {
        const jsonLdData = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    item: {
                        "@id": homePath
                            ? `${path.join(process.env.GATSBY_APP_DOMAIN, locale, homePath)}`
                            : process.env.GATSBY_APP_DOMAIN,
                        name: t("Home", { ns: "navigation" }),
                    },
                },
            ],
        };
        paths?.forEach((item, index) => {
            jsonLdData.itemListElement.push({
                "@type": "ListItem",
                position: index + 2,
                item: {
                    "@id": `${path.join(process.env.GATSBY_APP_DOMAIN, locale, item.url)}`,
                    name: item.title,
                },
            });
        });
        return jsonLdData;
    };

    const BREADCRUMB_JSON_LD = useMemo(() => generateJsonLd(paths, locale), [paths]);
    return (
        <Breadcrumb>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(BREADCRUMB_JSON_LD).replace("&quot;", `"`) }}
                data-test-id="Breadcrumb.JsonLd"
            />
            {!isMobile && (
                <a
                    href={homePath ? getLocalisedUrl(homePath) : getLocalisedUrl("/")}
                    onClick={(e) => e.preventDefault()}
                    className={classNames(style.link, style.home)}
                    data-test-id="Breadcrumb.Link"
                    aria-label={jnjFullBranded ? null : t("Home", { ns: "navigation" })}
                    title={jnjFullBranded ? null : t("Home", { ns: "navigation" })}
                >
                    <BreadcrumbNavigationInteraction name={navigationName} linkname={homeLink}>
                        <Breadcrumb.Path
                            onClick={(): void => navigate(getLocalisedUrl(homePath || "/"))}
                            name={t("Home", { ns: "navigation" })}
                            icon={jnjFullBranded ? null : mdiHome}
                            showDivider={false}
                        />
                    </BreadcrumbNavigationInteraction>
                </a>
            )}
            {paths?.map((path: { url: string; title: string; icon: string }, index: number) => {
                //On mobile, only the direct parent of the current page should be rendered inside of the breadcrumbs.
                //So the second to last element in the paths array.
                if (isMobile && index !== paths.length - 2 && paths.length != 1) {
                    return;
                }
                const pathTitle = sanitizeHtml(path?.title, {
                    allowedTags: [],
                });
                const footnote = footnotes && footnotes.find((footnote) => footnote?.text === path?.title);
                const formattedPathTitle = (footnote?.text_without_footnotes || pathTitle).replace(/&amp;/g, "&");

                return (
                    <a
                        key={path.title}
                        href={getLocalisedUrl(path.url)}
                        className={style.link}
                        data-test-id={`Breadcrumb.Link.${path.title}`}
                        onClick={(e) => (paths?.length === index + 1 || path.disabled) && e.preventDefault()}
                    >
                        <BreadcrumbNavigationInteraction name={navigationName} linkname={path.title}>
                            <Breadcrumb.Path
                                onClick={(): void => {
                                    if (isMobile) {
                                        navigate(getLocalisedUrl(path.url));
                                    }
                                }}
                                name={formattedPathTitle}
                                icon={path.icon}
                                active={isMobile ? true : paths?.length === index + 1}
                                disabled={path.disabled}
                            />
                        </BreadcrumbNavigationInteraction>
                    </a>
                );
            })}
        </Breadcrumb>
    );
};
